
import { computed, defineComponent, PropType, reactive, ref } from 'vue';
import useNumbers from '@/composables/useNumbers';
import { DecoratedPool } from '@/services/balancer/subgraph/types';
import useUserPoolsQuery from '@/composables/queries/useUserPoolsQuery';
import { useRoute } from 'vue-router';
import { TransactionResponse } from '@ethersproject/abstract-provider';
import useTransactions from '@/composables/useTransactions';
import useEthers from '@/composables/useEthers';
import useWeb3 from '@/services/web3/useWeb3';
import { balancerService } from '@/services/balancer/balancer.service';
import { bnum } from '@/lib/utils';
import { TransactionActionInfo } from '@/types/transactions';
import { useI18n } from 'vue-i18n';
import useLPTokenApprovalActions from '@/composables/useLPTokenApprovalActions';
import TokenFarmInput from '@/components/inputs/TokenInput/TokenFarmInput.vue';
import useUserFarmPoolsQuery from '@/composables/queries/useUserFarmPoolsQuery';
import useTokens from '@/composables/useTokens';
import { getAddress } from '@ethersproject/address';

/**
 * STATE
 */
const ACTIONTYPE = ['Stake', 'Withdraw', 'Claim'];
const ACTIONTITLE = [
  'Token Balance',
  'Withdrawable Token Balance',
  'Mined FLAKE'
];

export default defineComponent({
  components: {
    TokenFarmInput
  },

  props: {
    pool: { type: Object as PropType<DecoratedPool> },
    changeType: { type: String, default: '' },
    hasBar: { type: Boolean, default: false },
    barName: { type: String, default: '' },
    userClaimFlakeStatus: { type: Object }
  },

  setup(props) {
    const { getProvider, account } = useWeb3();
    const { fNum2 } = useNumbers();
    const route = useRoute();
    const { t } = useI18n();
    const id = route.params.farmId as string;
    const userPoolsQuery = useUserPoolsQuery();
    const { addTransaction } = useTransactions();
    const { txListener } = useEthers();
    const userFarmPoolsQuery = useUserFarmPoolsQuery();
    /**
     * STATE
     */
    const state = reactive({
      navCurIndex: 0,
      customSlippage: '',
      isCustomInput: false,
      inputAmount: '0'
    });
    const userPools = computed(() => userPoolsQuery.data.value);
    const { balanceFor } = useTokens();
    const bptBalance = computed((): string =>
      balanceFor(getAddress(id.split('_')[0]))
    );
    const userPoolShares = computed(() => {
      const shares = userPools.value?.pools.find(
        i => i.address.toLowerCase() === id.split('_')[0].toLowerCase()
      );
      return shares;
    });
    const { tokenApprovalActions } = useLPTokenApprovalActions(
      [id.split('_')[0].toLowerCase()],
      // TODO: workaound
      ref([state.inputAmount === '0' ? '1' : state.inputAmount])
    );

    const navBtnColor = computed(() => {
      if (props.changeType === 'stake') {
        return 'custom-black-300';
      }
      return 'custom-black-300';
    });

    const navBtnActiveColor = computed(() => {
      if (props.changeType === 'stake') {
        return 'custom-blue-50';
      } else if (props.changeType === 'boost') {
        return 'custom-green-50';
      }
      return 'custom-green-50';
    });

    const balanceNameColor = computed(() => {
      if (props.changeType === 'stake') {
        return 'text-gray-950';
      }
      return 'text-custom-black-350';
    });

    const userFarmPools = computed(
      () =>
        userFarmPoolsQuery.data.value?.pools.filter(
          i => i.pid === id.split('_')[1]
        )[0]
    );
    const actionTitle = computed(() => ACTIONTITLE[state.navCurIndex]);
    const actionTitleValue = computed(() => {
      let data = '';
      switch (state.navCurIndex) {
        case 0:
          data = bptBalance.value || '0';
          break;
        case 1:
          data = userFarmPools.value?.lpStaked
            ? bnum(userFarmPools.value?.lpStaked || '0')
                .div(Math.pow(10, 6))
                .toString()
            : '0';
          break;
        case 2:
          data = props.userClaimFlakeStatus
            ? bnum(props.userClaimFlakeStatus[0]?.wholePending || '0')
                .div(Math.pow(10, 18))
                .toString()
            : '0';
      }
      return data;
    });
    const actionBtnDisabled = computed(() => {
      let isDisabled;
      switch (state.navCurIndex) {
        case 0:
          isDisabled =
            bnum(bptBalance.value || '0').eq(0) || state.inputAmount === '0';
          break;
        case 1:
          isDisabled =
            bnum(userFarmPools.value?.lpStaked || '0').eq(0) ||
            state.inputAmount === '0';
          break;
        case 2:
          isDisabled = props.userClaimFlakeStatus
            ? bnum(props.userClaimFlakeStatus[0]?.wholePending || '0').eq(0)
            : true;
          break;
      }
      return isDisabled;
    });

    const chooseNavIndex = index => {
      state.navCurIndex = index;
      state.inputAmount = '0';
    };

    const handleInAmountChange = x => {
      state.inputAmount = x;
    };

    async function stakeLP(): Promise<TransactionResponse> {
      const provider = getProvider();
      // @ts-ignore
      const { pid } = props.pool;
      try {
        const formatAmount = bnum(state.inputAmount)
          .abs()
          .times(Math.pow(10, 6))
          .toString(16);
        const params = [pid, `0x${formatAmount}`, account.value];
        const tx = await balancerService.farmPools.normalFarm.deposit(
          provider,
          params
        );

        addTransaction({
          id: tx.hash,
          type: 'tx',
          action: 'deposit',
          summary: 'Deposit LP Token',
          details: {
            name: 'MyFarmPool'
          }
        });

        txListener(tx, {
          onTxConfirmed: async () => {
            userPoolsQuery.refetch.value();
            state.inputAmount = '0';
          },
          onTxFailed: () => {
            console.log('Deposit failed');
          }
        });

        return tx;
      } catch (e) {
        console.log(e);
        return Promise.reject('Deposit failed');
      }
    }

    async function withdrawLP(): Promise<TransactionResponse> {
      const provider = getProvider();
      // @ts-ignore
      const { pid } = props.pool;
      try {
        const formatAmount = bnum(state.inputAmount)
          .abs()
          .times(Math.pow(10, 6))
          .toString(16);
        const params = [pid, `0x${formatAmount}`, account.value];
        const tx = await balancerService.farmPools.normalFarm.withdraw(
          provider,
          params
        );

        addTransaction({
          id: tx.hash,
          type: 'tx',
          action: 'withdraw',
          summary: 'Withdraw LP Token Balance',
          details: {
            name: 'MyFarmPool'
          }
        });

        txListener(tx, {
          onTxConfirmed: async () => {
            userFarmPoolsQuery.refetch.value();
            state.inputAmount = '0';
          },
          onTxFailed: () => {
            console.log('Withdraw failed');
          }
        });

        return tx;
      } catch (e) {
        console.log(e);
        return Promise.reject('Withdraw failed');
      }
    }

    async function claimLP(): Promise<TransactionResponse> {
      const provider = getProvider();
      // @ts-ignore
      const { pid } = props.pool;
      try {
        const params = [pid, account.value];
        const tx = await balancerService.farmPools.normalFarm.harvest(
          provider,
          params
        );

        addTransaction({
          id: tx.hash,
          type: 'tx',
          action: 'claim',
          summary: 'Claim Rewards',
          details: {
            name: 'MyFarmPool'
          }
        });

        txListener(tx, {
          onTxConfirmed: async () => {
            // const poolDetails = await balancerService.pools.weighted.details(
            //   provider,
            //   tx
            // );
            // poolCreationState.poolId = poolDetails.id;
            // poolCreationState.poolAddress = poolDetails.address;
            // poolCreationState.needsSeeding = true;
            // saveState();
          },
          onTxFailed: () => {
            console.log('Claim failed');
          }
        });

        return tx;
      } catch (e) {
        console.log(e);
        return Promise.reject('Claim failed');
      }
    }

    const allActions = computed((): TransactionActionInfo[] => [
      {
        label: 'Stake',
        loadingLabel: 'Confirm staking in wallet',
        confirmingLabel: t('confirming'),
        action: () => stakeLP(),
        stepTooltip: 'Confirm staking into this pool'
      },
      {
        label: 'Withdraw',
        loadingLabel: 'Confirm withdrawing in wallet',
        confirmingLabel: t('confirming'),
        action: () => withdrawLP(),
        stepTooltip: 'Confirm withdrawing into this pool'
      },
      {
        label: 'Claim',
        loadingLabel: 'Confirm claiming in wallet',
        confirmingLabel: t('confirming'),
        action: () => claimLP(),
        stepTooltip: 'Confirm claiming into this pool'
      }
    ]);

    const actions = computed((): TransactionActionInfo[] => {
      const currentAction = allActions.value.filter(
        (action, index) => state.navCurIndex === index
      );
      return [...tokenApprovalActions, ...currentAction];
    });

    return {
      state,
      navBtnColor,
      navBtnActiveColor,
      balanceNameColor,
      userPoolShares,
      chooseNavIndex,
      fNum2,
      stakeLP,
      withdrawLP,
      actions,
      bnum,
      handleInAmountChange,
      ACTIONTYPE,
      actionTitle,
      actionTitleValue,
      actionBtnDisabled
    };
  }
});
